import * as React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import useAuth, { useCognitoAuth } from '../../hooks/useAuth'
import { isValidSession } from '../../utils/cognito'
import { isTemporaryToken, isValidToken } from '../../utils/jwt'
import { UserRole } from '../../enums'
import LoadingState from '../LoadingState'
import Dashboard from '../../layouts/Dashboard'
import NotFound from '../../pages/error/NotFound'
interface AuthGuardProps {
  roles?: UserRole[]
  children: React.ReactNode
}

export default function AuthGuard(props: AuthGuardProps) {
  return import.meta.env.VITE_APP_AUTH_PROVIDER === 'cognito' ? (
    <CognitoAuthGuard {...props} />
  ) : (
    <JwtAuthGuard {...props} />
  )
}

function CognitoAuthGuard({ children, roles = [] }: AuthGuardProps) {
  /**
   * The auth state.
   */
  const { isInitialized, isAuthenticated, currentUser } = useCognitoAuth()

  /**
   * The location.
   */
  const location = useLocation()

  const validSession = isValidSession()

  if ((isInitialized && !isAuthenticated) || !validSession) {
    const redirect = encodeURI(location.pathname + location.hash + location.search)

    return <Navigate to={`/auth/sign-in?redirect=${redirect}`} />
  }

  if (currentUser === undefined || currentUser === null) {
    return <LoadingState />
  }

  if (roles.length > 0 && !roles.includes(currentUser?.role)) {
    // return <Navigate to="/auth/404" />
    return (
      <Dashboard>
        <NotFound />
      </Dashboard>
    )
  }

  return <>{children}</>
}

function JwtAuthGuard({ children, roles }: AuthGuardProps) {
  const { isAuthenticated, isInitialized, currentUser } = useAuth()
  const location = useLocation()

  // TODO: useAuth() should provide get refreshToken() and get accessToken().
  const refreshToken = window.localStorage.getItem('refreshToken')
  const accessToken = window.localStorage.getItem('accessToken')

  const isRefreshTokenValid = refreshToken !== null ? isValidToken(refreshToken) : false

  const isAccessTokenTemporary = accessToken !== null ? isTemporaryToken(accessToken) : false

  if ((isInitialized && !isAuthenticated) || !isRefreshTokenValid || isAccessTokenTemporary) {
    const redirect = encodeURI(location.pathname + location.hash + location.search)
    return <Navigate to={`/auth/sign-in?redirect=${redirect}`} />
  }

  if (currentUser && roles && roles.indexOf(currentUser?.role) === -1) {
    return <Navigate to="/auth/404" />
  }

  return <React.Fragment>{children}</React.Fragment>
}
